import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNavigationContext } from "@features/navigation/context/NavigationContext";
import _ from "lodash";

// Custom Hooks
import { useImpersonation } from "../../hooks";

// Shared Components
import { CustomLoader } from "@shared/components";

// Sub-components
import { ImpersonationBadge, ProfileMenu, ImpersonateModal } from "./HeaderComponents";

// Assets
import logo from "@assets/Images/mconnect-logo.svg";
import profile from "@assets/Images/profile11.png";

// Constants
import { ALLOWED_IMPERSONATORS } from "../../utils";

// Styles removed - using Tailwind CSS

const Header = () => {
  const navigate = useNavigate();

  // Redux State
  const profileImage = useSelector((state) => state?.userDetails?.details?.Photo__c);
  const userDetails = useSelector((state) => state?.userDetails?.details);
  const impersonatedEmail = useSelector((state) => state?.impersonation?.impersonatedEmail);
  const originalUserEmail = useSelector((state) => state?.impersonation?.originalUserEmail);

  // Check if user can impersonate
  const canImpersonate =
    (userDetails && ALLOWED_IMPERSONATORS.includes(userDetails.Email)) || originalUserEmail;

  // Local State
  const [menuOpen, setMenuOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Custom Hooks
  const impersonation = useImpersonation(userDetails);
  const avatarRef = useRef(null);

  // Notification
  const { openNotifDrawer } = useNavigationContext();
  const unreadCount = useSelector((state) =>
    (state?.oaap?.notifications || []).filter((n) => !n.is_read).length
  );

  // Handlers
  const openModal = () => {
    setMenuOpen(false);
    impersonation.setError("");
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const handleStartImpersonation = async (email) => {
    const success = await impersonation.startImpersonation(email);
    if (success) {
      setModalOpen(false);
      setMenuOpen(false);
    }
    return success;
  };

  const handleStopImpersonation = async () => {
    await impersonation.stopImpersonation(originalUserEmail);
    setMenuOpen(false);
  };

  // Show loader if user details not loaded
  if (!userDetails || Object.keys(userDetails).length === 0) {
    return <CustomLoader loading={true} />;
  }

  return (
    <>
      <CustomLoader loading={impersonation.loading} />

      {/* Mobile-first PWA Header */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: 'transparent',
          borderBottom: '1px solid #f0f0f0',
          width: '100%',
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            minHeight: { xs: '56px', sm: '64px' },
            px: { xs: 2, sm: 3, lg: 4 },
          }}
        >
          {/* Logo */}
          <Box
            component="img"
            src={logo}
            alt="M-Connect Logo"
            sx={{
              height: { xs: '2rem', sm: '2.5rem', md: '2.25rem' },
              width: 'auto',
            }}
          />

          {/* Right: Notification + Profile */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>

            {/* Notification Bell */}
            <IconButton
              onClick={openNotifDrawer}
              aria-label="Notifications"
              sx={{
                p: { xs: '8px', sm: '10px' },
                ...(unreadCount > 0 && {
                  animation: "notifRing 1.8s ease infinite",
                  "@keyframes notifRing": {
                    "0%,100%": { transform: "rotate(0deg)" },
                    "10%":     { transform: "rotate(18deg)" },
                    "20%":     { transform: "rotate(-16deg)" },
                    "30%":     { transform: "rotate(14deg)" },
                    "40%":     { transform: "rotate(-10deg)" },
                    "50%":     { transform: "rotate(6deg)" },
                    "60%":     { transform: "rotate(-4deg)" },
                    "70%":     { transform: "rotate(2deg)" },
                    "80%":     { transform: "rotate(0deg)" },
                  },
                  transformOrigin: "top center",
                }),
              }}
            >
              <Badge
                badgeContent={unreadCount > 99 ? '99+' : unreadCount}
                color="error"
                max={99}
              >
                <NotificationsIcon
                  sx={{ color: '#3901ff', fontSize: { xs: '1.75rem', sm: '2rem' } }}
                />
              </Badge>
            </IconButton>

            {/* Profile Avatar */}
            <Box ref={avatarRef} sx={{ position: 'relative' }}>
              <IconButton
                onClick={() => {
                  if (canImpersonate) {
                    setMenuOpen((v) => !v);
                  } else {
                    navigate("/my-profile");
                  }
                }}
                aria-label="Open profile menu"
                sx={{ p: 0 }}
              >
                <Avatar
                  src={
                    !_.isEmpty(profileImage)
                      ? `data:image/png;base64,${profileImage}`
                      : profile
                  }
                  alt="Profile"
                  sx={{
                    width:  { xs: 54, sm: 48, md: 52 },
                    height: { xs: 54, sm: 48, md: 52 },
                    border: '2px solid #e8e8e8',
                  }}
                />
                {impersonatedEmail && <ImpersonationBadge />}
              </IconButton>

              {canImpersonate && (
                <ProfileMenu
                  open={menuOpen}
                  onClose={() => setMenuOpen(false)}
                  onOpenImpersonate={openModal}
                  onStopImpersonation={handleStopImpersonation}
                  impersonating={!!impersonatedEmail}
                  loading={impersonation.loading}
                  canImpersonate={canImpersonate}
                />
              )}

              <ImpersonateModal
                open={modalOpen}
                onClose={closeModal}
                onSubmit={handleStartImpersonation}
                loading={impersonation.loading}
                error={impersonation.error}
                setError={impersonation.setError}
              />
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
    </>
  );
};

export default Header;
